<template>
  <PreviewBase
    v-if="isJobProfile"
    :loading="loading || pageLoading"
  >
    <template #loading>
      <JobProfilePreviewSkeleton />
    </template>

    <div class="d-flex flex-column ga-1">
      <div class="text-h1 text-textDarkest text-center">
        {{ jobProfile?.title }}
      </div>

      <div
        v-if="jobProfile?.title"
        class="d-flex justify-center"
      >
        <JobProfileStatus
          :is-hris-profile="isHrisProfile"
          :status="jobProfile?.status"
          :is-template="isTemplate"
        />
      </div>

      <JobProfileMeta
        :profile="jobProfile"
        class="justify-center"
      />

      <div
        v-if="isTemplate && jobProfile?.memo"
        class="text-body-3 text-textLight text-center"
      >
        {{ jobProfile?.memo }}
      </div>

      <div
        v-if="jobProfile?.hris_reference_ulid"
        class="text-body-3 text-textLight text-center"
      >
        <router-link :to="{ name: 'job-profile-hris-view', params: { id: jobProfile?.hris_reference_ulid } }">
          Workday job profile
        </router-link>
      </div>
    </div>

    <div
      class="d-flex ga-2"
      :class="{ 'opacity-50 pointer-events-none': !hasEditProfilePermission }"
    >
      <router-link
        class="flex-grow-1"
        :to="{
          name: type === 'jobProfileHris' ? 'job-profile-hris-view' : 'job-profile-view',
          params: { id: ulid },
        }"
      >
        <v-btn
          class="w-100"
          variant="flat"
          color="primary"
          rounded="lg"
          @click="onOpenEditorClick"
        >
          Open in Editor
        </v-btn>
      </router-link>
    </div>

    <JobProfilePreviewArchitectureAlerts
      v-if="type === 'jobProfileHris' && ulid && route.name === 'job-architecture'"
      :profile-ulid="ulid"
    />

    <div class="job-content">
      <div
        v-for="(field, i) in nonEmptyFields"
        :key="field.id"
      >
        <v-divider
          v-if="i > 0"
          class="opacity-100 mt-3 mb-4"
        />

        <div class="text-body-1 text-textDarkest">
          {{ field.label }}
        </div>

        <DocumentPreviewFieldHtml :html="field.headerHtml" />
        <DocumentPreviewFieldHtml :html="field.html" />
        <DocumentPreviewFieldHtml :html="field.footerHtml" />
      </div>
    </div>
  </PreviewBase>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import { useAppRoute } from '@/stores/app/route';
import { useTracking } from '@/stores/tracking/tracking';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import PreviewBase from '@/components/shared/PreviewBase.vue';
import {
  getJpLibPageFromRoute,
  getJpLibProfileType,
  isJpLibraryRoute,
} from '@/helpers/jobProfileLibraryTracking';
import JobProfilePreviewSkeleton from '@/components/jobProfiles/editor/JobProfilePreviewSkeleton.vue';
import DocumentPreviewFieldHtml from '@ui/components/general/DocumentPreviewFieldHtml/DocumentPreviewFieldHtml.vue';
import JobProfileStatus from '@/components/jobProfiles/editor/JobProfileStatus.vue';
import JobProfileMeta from '@/components/jobProfiles/editor/JobProfileMeta.vue';
import JobProfilePreviewArchitectureAlerts from '@/components/jobArchitecture/JobProfilePreviewArchitectureAlerts.vue';

const { route } = storeToRefs(useAppRoute());
const { pageLoading } = storeToRefs(useAppLoading());
const {
  jobProfile, nonEmptyFields, isTemplate, isHrisProfile, hasEditAccess,
} = storeToRefs(useCurrentJobProfile());
const { checkRbacPermissions } = useAppAuthData();

const ulid = computed(() => route.value?.query?.preview);
const type = computed(() => route.value?.query?.type);
const loading = ref(false);

const libraryProfileType = computed(() => getJpLibProfileType({
  isHris: isHrisProfile.value,
  isTemplate: isTemplate.value,
}));

const hasEditProfilePermission = computed(() => {
  if (isTemplate.value) {
    return checkRbacPermissions(['createProfileTemplate']);
  }

  if (isHrisProfile.value) {
    return checkRbacPermissions(['initHrisProfileEditor']);
  }

  return checkRbacPermissions(['saveDraftProfileRevision']) || hasEditAccess.value;
});

const onOpenEditorClick = () => {
  if (route.value?.name === 'job-architecture' && type.value === 'jobProfileHris') {
    useTracking().jaListOpenedJobProfileFromSidebar();
  } else if (isJpLibraryRoute(route.value?.name)) {
    useTracking().jpLibOpenedInEditor({
      page: getJpLibPageFromRoute(route.value?.name),
      profileType: libraryProfileType.value,
    });
  }

  loading.value = true;
};

const isJobProfile = computed(() => ['jobProfile', 'jobProfileHris'].includes(type.value));
const load = async () => {
  await useCurrentJobProfile().loadJobProfile({
    ulid: ulid.value,
    isHris: type.value === 'jobProfileHris',
    rescore: false,
  });

  if (isJpLibraryRoute(route.value?.name)) {
    useTracking().jpLibOpenedPreview({
      page: getJpLibPageFromRoute(route.value?.name),
      profileType: libraryProfileType.value,
    });
  }
};

const reset = () => {
  useCurrentJobProfile().resetJobProfile();
  loading.value = false;
};

watch(ulid, () => {
  if (ulid.value && isJobProfile.value) {
    load();
  } else {
    reset();
  }
}, { immediate: true });
</script>
